import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import styles from '../manage_wareHoue/style.module.scss';
import { EditIcon, ProjectNameIcon, StatusCheckIcon } from 'assets';
import { useSelector } from 'react-redux';
import GenericTable from 'shared/components/common/table/table';
import Heading from 'shared/components/common/heading/Heading';
import OverlayLoading from 'shared/screen_loader/overlay_loader/OverlayLoading';
import SearchBar from 'shared/components/common/searchbar/SearchBar';
import { GetShopifyLocationsQuery, UpdateShopifyWarehouseMutation, SyncShopifyMutation } from 'shared/services/shopify_service/ShopifyService';
import { GetAllWarehouse } from 'shared/services/ware_house_service/WareHouseService';
import Select from 'react-select';

export default function ShopifyLocations() {
    const {
        theme: { theme }
    } = useSelector((state) => state.root);

    const [searchResults, setSearchResults] = useState([]);

    // We will use a unique key string to identify the row being edited
    // Format: "index-id"
    const [editingRowKey, setEditingRowKey] = useState(null);
    const [selectedWarehouse, setSelectedWarehouse] = useState({});

    const { isLoading, data, isError } = GetShopifyLocationsQuery();
    const { data: warehouseList } = GetAllWarehouse();
    const { mutate: updateWarehouse, isLoading: updateLoading } = UpdateShopifyWarehouseMutation();
    const { mutate: syncShopify, isLoading: syncLoading } = SyncShopifyMutation();

    const warehouseOptions = React.useMemo(() => {
        if (!warehouseList) return [];
        return warehouseList.map(w => ({
            label: `${w.name || ''} (${w.code || ''})`,
            value: w._id
        }));
    }, [warehouseList]);

    const handleSave = (location, rowKey) => {
        const wh = selectedWarehouse[rowKey];
        if (!wh) {
            console.error("No warehouse selected for this row.");
            return;
        }

        // Broad ID detection - checking every possible field
        const shopify_id =
            location.id ||
            location._id ||
            location.shopify_location_id ||
            location.location_id ||
            location.legacyResourceId ||
            location.admin_graphql_api_id;

        console.log("Handing over to Service:", {
            shopify_location_id: shopify_id,
            warehouse_id: wh.value
        });

        if (!shopify_id) {
            console.error("Could not find any ID in the location object:", location);
            alert("Error: Shopify Location ID not found in data. Check console.");
            return;
        }

        updateWarehouse(
            {
                shopify_location_id: String(shopify_id), // Ensure it's a string
                warehouse_id: wh.value
            },
            {
                onSuccess: () => {
                    setEditingRowKey(null);
                }
            }
        );
    };

    const columns = React.useMemo(
        () => [
            {
                Header: (
                    <div className={classNames(`${theme}FontColor w-100`)}>
                        <ProjectNameIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Shopify Locations
                    </div>
                ),
                accessor: 'name',
                Cell: ({ row }) => <div className="w-100">{row.original?.name}</div>
            },
            {
                Header: (
                    <div className={classNames(`${theme}FontColor w-100`)}>
                        <ProjectNameIcon className={classNames(styles.TableHeadingIcon, `${theme}SvgIcon`)} style={{ marginRight: '6px' }} />
                        Linked Warehouse
                    </div>
                ),
                accessor: 'warehouse',
                Cell: ({ row }) => {
                    // Create a truly unique key for this row in the UI
                    const rowKey = `row-${row.index}`;
                    const isEditing = editingRowKey === rowKey;
                    const mappedWh = row.original?.mapped_warehouse || row.original?.warehouse;

                    if (isEditing) {
                        return (
                            <div className="d-flex align-items-center gap-2" style={{ minWidth: 260 }}>
                                <div style={{ flex: 1 }}>
                                    <Select
                                        options={warehouseOptions}
                                        isSearchable
                                        isClearable
                                        placeholder="Select Warehouse"
                                        value={selectedWarehouse[rowKey] || null}
                                        menuPortalTarget={document.body}
                                        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                        onChange={(item) => {
                                            setSelectedWarehouse(prev => ({
                                                ...prev,
                                                [rowKey]: item
                                            }));
                                        }}
                                    />
                                </div>
                                <button
                                    onClick={() => handleSave(row.original, rowKey)}
                                    disabled={updateLoading || !selectedWarehouse[rowKey]}
                                    style={{
                                        background: '#1a8a56',
                                        border: 'none',
                                        borderRadius: '6px',
                                        padding: '6px 12px',
                                        color: '#fff',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {updateLoading ? '...' : 'Save'}
                                </button>
                                <button
                                    onClick={() => setEditingRowKey(null)}
                                    style={{
                                        background: 'transparent',
                                        border: '1px solid #aaa',
                                        borderRadius: '6px',
                                        padding: '6px 10px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    ✕
                                </button>
                            </div>
                        );
                    }

                    return (
                        <div className="d-flex align-items-center" style={{ gap: 8 }}>
                            <div className="d-flex flex-column">
                                <span style={{ color: mappedWh ? '#1a8a56' : '#888', fontStyle: mappedWh ? 'normal' : 'italic', fontSize: 13, fontWeight: mappedWh ? 600 : 400 }}>
                                    {mappedWh?.name
                                        ? `${mappedWh.name} (${mappedWh.code || ''})`
                                        : 'Not linked'}
                                </span>
                                {mappedWh && (
                                    <span style={{ fontSize: '10px', color: '#1a8a56', fontWeight: 700 }}>● Active Link</span>
                                )}
                            </div>
                            <EditIcon
                                className="pointer"
                                style={{ cursor: 'pointer', flexShrink: 0 }}
                                onClick={() => {
                                    setEditingRowKey(rowKey);
                                    // Pre-fill if already linked
                                    if (mappedWh?._id) {
                                        setSelectedWarehouse(prev => ({
                                            ...prev,
                                            [rowKey]: {
                                                label: `${mappedWh.name} (${mappedWh.code || ''})`,
                                                value: mappedWh._id
                                            }
                                        }));
                                    }
                                }}
                            />
                        </div>
                    );
                }
            }
        ],
        [theme, editingRowKey, selectedWarehouse, warehouseOptions, updateLoading]
    );

    useEffect(() => {
        if (data) {
            setSearchResults(data);
        }
    }, [data]);

    return (
        <div className={classNames(styles.UserContainer, 'text-start')}>
            <SearchBar
                searchKey="name"
                suggestionsSource={data}
                onSearchChange={setSearchResults}
            />
            <div className={classNames(styles.Header, 'd-flex justify-content-between align-items-center mb-3')}>
                <Heading heading={'Shopify Locations'} headingStyle={styles.Heading} />
                <button
                    onClick={() => syncShopify()}
                    disabled={syncLoading}
                    className="d-flex align-items-center gap-2"
                    style={{
                        background: '#1a8a56',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '10px 20px',
                        fontWeight: 600,
                        cursor: syncLoading ? 'not-allowed' : 'pointer',
                        opacity: syncLoading ? 0.7 : 1,
                        transition: 'all 0.3s ease',
                        boxShadow: '0 4px 12px rgba(26, 138, 86, 0.25)',
                    }}
                    onMouseOver={(e) => {
                        if (!syncLoading) {
                            e.currentTarget.style.transform = 'translateY(-1px)';
                            e.currentTarget.style.boxShadow = '0 6px 16px rgba(26, 138, 86, 0.35)';
                        }
                    }}
                    onMouseOut={(e) => {
                        if (!syncLoading) {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(26, 138, 86, 0.25)';
                        }
                    }}
                >
                    <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{
                            animation: syncLoading ? 'spin 1s linear infinite' : 'none',
                            transformOrigin: 'center'
                        }}
                    >
                        <path d="M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                    </svg>
                    {syncLoading ? 'Syncing...' : 'Sync Locations'}
                </button>
            </div>

            <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>

            {isError && (
                <div style={{ color: 'red', padding: '12px 0' }}>
                    Failed to load Shopify locations. Please check your Shopify integration.
                </div>
            )}

            <GenericTable
                columns={columns}
                data={searchResults || []}
                TDTextStyle={classNames(styles.TDTextStyle)}
                TableWrapperStyle={classNames(styles.Table)}
            />
            {isLoading && <OverlayLoading />}
        </div>
    );
}
